import { createContext, useContext, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, X, Check } from 'lucide-react'
import { startTrialCheckout } from '../lib/checkout'

// Card-wall reutilizable del modelo "no Free permanente": cuando una acción de
// valor (publicar, lanzar Meta, IA premium) devuelve plan_required/account_paused,
// cualquier componente llama showTrialWall({ title, message }) y aparece este modal
// con CTA directo a la prueba de 14 días (Growth).
const TrialWallContext = createContext(null)

export const useTrialWall = () => useContext(TrialWallContext) || { showTrialWall: () => {} }

const DEFAULTS = {
  title: 'Empieza tu prueba gratis',
  message: 'Esta acción forma parte de un plan. Pruébalo 14 días gratis y desbloquéalo todo.',
}

const BULLETS = [
  '14 días gratis, cancela cuando quieras',
  'Publica landings y quizzes sin límite',
  'Lanza campañas en Meta con IA',
]

export function TrialWallProvider({ children }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  const showTrialWall = useCallback((opts = {}) => {
    setError(null)
    setState({ ...DEFAULTS, ...opts })
  }, [])

  const close = () => {
    if (loading) return
    setState(null)
    setError(null)
  }

  const start = async () => {
    setLoading(true)
    setError(null)
    try {
      await startTrialCheckout('growth')
    } catch (e) {
      setError(e.message || 'No pudimos iniciar la prueba.')
      setLoading(false)
    }
  }

  return (
    <TrialWallContext.Provider value={{ showTrialWall }}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          onClick={close}
        >
          <div
            className="bg-surface border border-line rounded-2xl p-6 w-full max-w-md relative"
            onClick={e => e.stopPropagation()}
          >
            <button onClick={close} className="absolute top-4 right-4 text-muted hover:text-white transition-colors">
              <X size={18} />
            </button>
            <div className="w-11 h-11 rounded-xl bg-aurora-violet/15 border border-aurora-violet/25 flex items-center justify-center text-aurora-violet-soft mb-4">
              <Sparkles size={20} />
            </div>
            <h2 className="font-display text-xl text-white mb-2">{state.title}</h2>
            <p className="text-muted text-sm leading-relaxed mb-5">{state.message}</p>
            <ul className="space-y-2 mb-6">
              {BULLETS.map(f => (
                <li key={f} className="flex items-center gap-2 text-sm text-white/80">
                  <Check size={14} className="text-aurora-cyan shrink-0" />{f}
                </li>
              ))}
            </ul>
            {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
            <button
              onClick={start}
              disabled={loading}
              className="btn-primary-aurora w-full !py-3 disabled:opacity-60"
            >
              {loading ? 'Abriendo…' : (state.ctaLabel || 'Empieza tu prueba de 14 días gratis')}
            </button>
            <button
              onClick={() => { close(); navigate('/pricing') }}
              className="w-full text-center text-muted hover:text-white text-xs mt-3 transition-colors"
            >
              Ver todos los planes
            </button>
          </div>
        </div>
      )}
    </TrialWallContext.Provider>
  )
}
